// @flow
import React, { Component } from 'react';
import { Grid, Icon, Responsive } from 'semantic-ui-react';
import { assign, some, uniqBy, uniq, orderBy } from 'lodash';
import { parse, stringify } from 'query-string';
import DataGrid, {
    Column,
    ColumnChooser,
    ColumnFixing,
    Editing,
    Export,
    FilterRow,
    FilterPanel,
    Grouping,
    GroupPanel,
    HeaderFilter,
    Pager,
    Paging,
    SearchPanel,
    Sorting,
    StateStoring,
} from 'devextreme-react/data-grid';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { getDashboards, getMasterFilterSession, deleteDashboard } from '../../modules/client';
import FloatingActionsHover from '../molecules/FloatingActionsHover';
import MasterFilterModal from '../molecules/MasterFilterModal';
import ShareModal from '../molecules/ShareModal';
import DeleteModal from '../molecules/DeleteModal';
import type { LocationShape, Match, RouterHistory } from 'react-router-dom';
import { REPORTING_API } from '../../modules/consts';

type Props = {
    clientId: Guid,
    dashboardsExist: boolean,
    dashboards: Dashboard[],
    dashboardsError: ?string,
    history: RouterHistory,
    location: LocationShape & { search: string },
    match: Match & { params: { dashboardId: Guid } },
    onDeleteDashboard: (Guid, Guid) => Promise<void>,
    onPatchDashboard: (
        dashboardId: Guid,
        clientId: Guid,
        options: { description: ?string, statusId: ?number, isMobile: ?boolean }
    ) => Promise<void>,
    onGetDashboards: (Guid) => Promise<Dashboard[]>,
    onGetVersionFlags: (Guid) => Promise<Object[]>,
    onSavePermissions: (Guid, Guid, Array<{ id: Guid, selected: boolean }>) => Promise<void>,
    onShareDashboard: (Guid, any) => Promise<void>,
    user: User,
    users: UserPermissions[],
    versionFlags: Object[],
};

export default class Home extends Component<
    Props,
    {
        columnHidingEnabled: boolean,
        deleteAction: string,
        loading: boolean,
        selectedDashboardId: ?Guid,
        showDeleteModal: boolean,
        showShareModal: boolean,
        showMasterFilterModal: boolean,
        selectedMasterFilter: ?Guid,
        masterFilterEnabled: boolean,
    }
> {
    constructor() {
        super();
        this.state = {
            columnHidingEnabled: window.innerWidth <= Responsive.onlyTablet.maxWidth,
            deleteAction: 'Undelete',
            loading: true,
            selectedDashboardId: null,
            showDeleteModal: false,
            showShareModal: false,
            showMasterFilterModal: false,
            selectedMasterFilter: null,
            masterFilterEnabled: false,
            filterSessions: [],
        };
        this.onToolbarPreparing = this.onToolbarPreparing.bind(this);
        this.onCloseMasterFilter = this.onCloseMasterFilter.bind(this);
        this.refreshFilterSessionDropdown = this.refreshFilterSessionDropdown.bind(this);
    }
    isAdmin = false;
    isManager = false;
    isSuperUser = false;
    componentDidMount() {
        this.getData();
        this.isAdmin =
            this.props.user.isAdmin.length &&
            this.props.user.isAdmin.indexOf(this.props.clientId) !== -1;
        this.isManager =
            this.props.user.isManager.length &&
            this.props.user.isManager.indexOf(this.props.clientId) !== -1;
        this.isSuperUser = this.props.user.isSuperUser;
        this.initializeMasterFilter();
    }
    initializeMasterFilter() {
        this.props.onGetVersionFlags(this.props.clientId).then(() => {
            const enabled = this.props.versionFlags.some(
                (versionFlag) =>
                    versionFlag.featureName === 'enableMasterFilter' && versionFlag.enabled
            );
            this.setState({ masterFilterEnabled: enabled }, () => {
                if (this.dxFilterDropdownInstance !== undefined) {
                    this.dxFilterDropdownInstance.option({
                        visible: enabled,
                    });
                }
                if (this.dxCleanFilterButtonInstance !== undefined) {
                    this.dxCleanFilterButtonInstance.option({
                        visible: enabled,
                    });
                }
                this.refreshFilterSessionDropdown();
            });
        });
    }
    componentDidUpdate(prevProps: Props) {
        if (this.props.clientId !== prevProps.clientId) {
            this.initializeMasterFilter();
            this.getData().then(() => this.setDefaultCategory());
        }
    }
    getData(): Promise<void> {
        return this.props.onGetDashboards(this.props.clientId).then(() => {
            this.setDefaultCategory();
        });
    }
    refreshFilterSessionDropdown(): Promise<void> {
        if (!this.state.masterFilterEnabled) {
            if (this.dxFilterDropdownInstance !== undefined) {
                this.dxFilterDropdownInstance.option('items', []);
            }
            this.setState({ filterSessions: [] });
            return;
        }
        getMasterFilterSession(this.props.clientId).then((sessions) => {
            this.setState({ filterSessions: sessions });
            if (this.dxFilterDropdownInstance !== undefined) {
                const newSessionList = sessions.map((el) => ({
                    value: el.SessionUid,
                    text: el.TemplateName,
                }));
                this.dxFilterDropdownInstance.option('items', newSessionList);
            }
        });
    }
    onCellClick = (params) => {
        if (params.data) {
            switch (params.column.caption) {
                case 'Edit':
                    return params.data.isStandard && !this.isSuperUser
                        ? null
                        : window.open(
                              `${REPORTING_API}/home/editor?dashboardId=${params.value}&clientId=${this.props.clientId}`,
                              '_blank'
                          );
                case 'Copy':
                    if (params.data.isStandard && !this.isSuperUser) {
                        return;
                    }
                    window.open(
                        `${REPORTING_API}/home/copy?dashboardId=${params.data.id}&clientId=${this.props.clientId}`,
                        '_blank'
                    );
                    return setTimeout(() => this.props.onGetDashboards(this.props.clientId), 1500);
                case 'Share':
                    return this.setState({
                        showShareModal: true,
                        selectedDashboardId: params.data.id,
                    });
                case 'Status':
                    if (params.data.isStandard && !this.isSuperUser) {
                        return;
                    }
                    return this.setState({
                        showDeleteModal: true,
                        selectedDashboardId: params.data.id,
                        deleteAction: params.data.statusId == 'Enabled' ? 'Delete' : 'Undelete',
                    });
            }
        }
    };
    onRowUpdating = (params) => {
        params.cancel = this.props.onPatchDashboard(params.oldData.id, this.props.clientId, {
            description: params.newData.description,
            isMobile: params.newData.isMobile,
            statusId: null,
        });
    };
    onShare(exportType: string) {
        this.setState({ showShareModal: true });
    }
    onFilterChanged(e) {
        this.setState({ selectedMasterFilter: e.value });
    }
    onToolbarPreparing(e) {
        this.toolbarOptions = e.toolbarOptions;
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                widget: 'dxSelectBox',
                options: {
                    width: 200,
                    placeholder: 'Choose Filter...',
                    items: [],
                    displayExpr: 'text',
                    valueExpr: 'value',
                    value: 'CustomerStoreState',
                    onValueChanged: this.onFilterChanged.bind(this),
                    visible: this.state.masterFilterEnabled,
                    onInitialized: (args: any) => {
                        this.dxFilterDropdownInstance = args.component;
                        this.refreshFilterSessionDropdown();
                    },
                },
            },
            {
                location: 'before',
                widget: 'dxButton',
                options: {
                    text: 'Clear Filter',
                    visible: this.state.masterFilterEnabled,
                    onClick: () => {
                        //this.setState({ selectedMasterFilter: null });
                        this.dxFilterDropdownInstance.reset();
                    },
                    onInitialized: (args: any) => {
                        this.dxCleanFilterButtonInstance = args.compenent;
                    },
                },
            }
        );
    }
    onCloseMasterFilter() {
        this.setState({ showMasterFilterModal: false });
    }
    setDefaultCategory() {
        const anyDashboards = this.props.dashboards.length > 0;
        const dashboardNotSelected = !parse(this.props.location.search || '').category;
        if (anyDashboards && dashboardNotSelected) {
            const query = parse(this.props.location.search);
            const firstCategory = this.props.dashboards[0].categoryName;
            const queryWithCategory = assign({}, query, { category: firstCategory });
            this.props.history.replace(
                assign({}, this.props.location, { search: stringify(queryWithCategory) })
            );
        }
    }
    renderNameColumn = (data) => {
        const masterFilterSessionQuery =
            this.state.selectedMasterFilter !== null && this.state.masterFilterEnabled
                ? `&FilterSessionUid=${this.state.selectedMasterFilter}`
                : '';
        return (
            <div>
                {data.data.globalFilterCompatible && this.state.masterFilterEnabled ? (
                    <i className="small blue filter icon"></i>
                ) : null}
                <a
                    href={`${REPORTING_API}/home?dashboardId=${data.data.id}&clientId=${this.props.clientId}${masterFilterSessionQuery}`}
                    target="_blank"
                >
                    {data.value}
                </a>
            </div>
        );
    };
    render() {
        const {
            columnHidingEnabled,
            showShareModal,
            showDeleteModal,
            selectedDashboardId,
        } = this.state;
        const currentQuery = parse(this.props.location.search || '');
        const clientId = currentQuery.clientId;
        const category = currentQuery.category; // better to get both into array
        const subCategory = currentQuery.subCategory; // better to get both into array
        const dashboardParams = stringify(assign(currentQuery, { id: selectedDashboardId }));
        let actionOptions = [];

        if (this.isAdmin) {
            actionOptions.push({
                icon: 'edit',
                onClick: () =>
                    window.open(`${REPORTING_API}/home/editor?${dashboardParams}`, '_blank'),
            });
        }
        if (this.state.masterFilterEnabled) {
            actionOptions.push({
                icon: 'filter',
                onClick: () => this.setState({ showMasterFilterModal: true }),
            });
        }

        let usersWithDashboard = [];
        if (selectedDashboardId) {
            usersWithDashboard = this.props.users
                .filter(
                    (user) =>
                        user.id.toLowerCase() != this.props.user.id.toLowerCase() && !user.isAdmin
                )
                .map((user) => ({
                    email: user.email,
                    firstName: user.firstName,
                    hasObject: some(
                        user.dashboards,
                        (dashboardId) => dashboardId === selectedDashboardId
                    ),
                    id: user.id,
                    isManager: user.isManager,
                    lastName: user.lastName,
                }));
        }
        const exportTypes = [
            { text: 'PDF', icon: 'file pdf outline', value: 'pdf' },
            { text: 'Image', icon: 'image', value: 'image' },
        ];
        const dashboardData = this.props.dashboards.map((dashboard) => ({
            categoryName: dashboard.categoryName,
            description: dashboard.description,
            id: dashboard.id,
            name: dashboard.name,
            isStandard: dashboard.isStandard,
            isMobile: dashboard.isMobile,
            statusId: dashboard.statusId == 1 ? 'Enabled' : 'Deleted',
            subCategoryName: dashboard.subCategoryName,
            globalFilterCompatible: dashboard.globalFilterCompatible,
        }));
        const dashboardDataFiltered = dashboardData.filter(
            (dashboard) => dashboard.categoryName === category
        );
        const pageSizes = [10, 25, 50, 100];
        return (
            <div style={{ height: '100%' }}>
                <Responsive
                    onUpdate={() =>
                        this.setState((prevState) => ({
                            columnHidingEnabled:
                                window.innerWidth <= Responsive.onlyTablet.maxWidth,
                        }))
                    }
                />
                {this.props.dashboardsExist ? (
                    <DataGrid
                        allowColumnReordering
                        allowColumnResizing
                        columnHidingEnabled={columnHidingEnabled}
                        dataSource={dashboardDataFiltered}
                        defaultFilterValue={['statusId', '=', 'Enabled']}
                        hoverStateEnabled
                        id={'grid'}
                        onCellClick={this.onCellClick}
                        onContentReady={(params) => (this.gridApi = params.compenent)}
                        onRowUpdating={this.onRowUpdating}
                        showBorders
                        onToolbarPreparing={this.onToolbarPreparing}
                    >
                        <ColumnChooser enabled mode="select" />
                        <ColumnFixing enabled />
                        <Editing mode="cell" allowUpdating={this.isAdmin} />
                        <Export enabled={true} fileName={'Dashboards'} />
                        <FilterRow visible />
                        <FilterPanel visible={this.isAdmin} />
                        <HeaderFilter visible />
                        <Sorting mode="multiple" />
                        <StateStoring
                            enabled
                            type="localStorage"
                            storageKey="dashboard-grid-storage"
                        />
                        <Column
                            allowEditing={false}
                            cellRender={this.renderNameColumn}
                            dataField="name"
                            dataType="string"
                            headerFilter={{ allowSearch: true }}
                        />
                        <Column
                            allowEditing={this.isAdmin}
                            dataField="description"
                            dataType="string"
                            headerFilter={{ allowSearch: true }}
                        />
                        <Column
                            allowEditing={false}
                            caption="SubCategory"
                            dataField="subCategoryName"
                            dataType="string"
                            headerFilter={{ allowSearch: true }}
                        />
                        <Column
                            alignment="center"
                            allowEditing={false}
                            allowExporting={false}
                            allowFiltering={false}
                            caption="Edit"
                            cellRender={(data) => (
                                <Icon
                                    disabled={data.data.isStandard && !this.isSuperUser}
                                    link={!data.data.isStandard || this.isSuperUser}
                                    name="edit"
                                    size="large"
                                />
                            )}
                            dataField="id"
                            dataType="string"
                            showInColumnChooser={this.isAdmin}
                            visible={this.isAdmin}
                        />
                        <Column
                            alignment="center"
                            allowEditing={false}
                            allowExporting={false}
                            allowFiltering={false}
                            caption="Copy"
                            cellRender={(data) => (
                                <Icon
                                    disabled={data.data.isStandard && !this.isSuperUser}
                                    link={!data.data.isStandard || this.isSuperUser}
                                    name="copy"
                                    size="large"
                                />
                            )}
                            dataField="id"
                            dataType="string"
                            showInColumnChooser={this.isAdmin}
                            visible={this.isAdmin}
                        />
                        <Column
                            alignment="center"
                            allowEditing={false}
                            allowExporting={false}
                            allowFiltering={false}
                            caption="Share"
                            cellRender={(data) => <Icon link name="add user" size="large" />}
                            dataField="id"
                            dataType="string"
                            showInColumnChooser={this.isAdmin || this.isManager}
                            visible={this.isAdmin || this.isManager}
                        />
                        <Column
                            allowEditing={this.isSuperUser}
                            caption="Mobile"
                            dataField="isMobile"
                            dataType="boolean"
                        />
                        <Column
                            alignment="center"
                            allowEditing={false}
                            caption="Status"
                            cellRender={(data) =>
                                data.value === 'Enabled' ? (
                                    <Icon
                                        color="red"
                                        disabled={data.data.isStandard && !this.isSuperUser}
                                        link={!data.data.isStandard || this.isSuperUser}
                                        name="delete"
                                        size="large"
                                    />
                                ) : (
                                    <Icon
                                        color="black"
                                        disabled={data.data.isStandard && !this.isSuperUser}
                                        link={!data.data.isStandard || this.isSuperUser}
                                        name="undo"
                                        size="large"
                                    />
                                )
                            }
                            dataField="statusId"
                            dataType="string"
                            showInColumnChooser={this.isAdmin}
                            visible={this.isAdmin}
                        />
                        <GroupPanel visible />
                        <SearchPanel visible highlightCaseSensitive />
                        <Grouping autoExpandAll={false} />
                        <Pager allowedPageSizes={pageSizes} showPageSizeSelector />
                        <Paging defaultPageSize={50} />
                    </DataGrid>
                ) : (
                    <Grid centered columns={1} style={{ height: '100%' }}>
                        <Grid.Row verticalAlign="middle">
                            <Grid.Column textAlign="center">
                                {this.props.dashboardsError ? (
                                    <span>
                                        <Icon color="red" name="warning sign" />
                                        {this.props.dashboardsError}
                                    </span>
                                ) : (
                                    'You do not have any dashboards.'
                                )}
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                )}

                {actionOptions.length ? (
                    <Responsive as={React.Fragment} {...Responsive.onlyComputer}>
                        <FloatingActionsHover options={actionOptions} />
                    </Responsive>
                ) : null}

                <ShareModal
                    attachmentId={selectedDashboardId}
                    attachmentType={'dashboard'}
                    clientId={clientId}
                    exportTypes={exportTypes}
                    onClose={() => this.setState({ showShareModal: false })}
                    onSavePermissions={(
                        clientId: Guid,
                        dashboardId: Guid,
                        permissions: Array<{ id: Guid, selected: boolean }>
                    ) => {
                        this.setState({ showShareModal: false });
                        return this.props.onSavePermissions(clientId, dashboardId, permissions);
                    }}
                    onShare={this.props.onShareDashboard}
                    open={showShareModal}
                    users={usersWithDashboard}
                />

                <DeleteModal
                    action={this.state.deleteAction}
                    onCancel={() => this.setState({ showDeleteModal: false })}
                    onConfirm={() => {
                        if (this.state.selectedDashboardId) {
                            if (this.state.deleteAction == 'Delete') {
                                this.props
                                    .onDeleteDashboard(this.state.selectedDashboardId, clientId)
                                    .then(() => this.setState({ showDeleteModal: false }));
                            } else {
                                this.props
                                    .onPatchDashboard(this.state.selectedDashboardId, clientId, {
                                        description: null,
                                        statusId: 1,
                                    })
                                    .then(() => this.setState({ showDeleteModal: false }));
                            }
                        }
                    }}
                    open={showDeleteModal}
                    type="Dashboard"
                />

                {this.state.masterFilterEnabled ? (
                    <MasterFilterModal
                        clientId={clientId}
                        open={this.state.showMasterFilterModal}
                        onClose={this.onCloseMasterFilter}
                        onRefreshFilterSessions={this.refreshFilterSessionDropdown}
                        filterSessions={this.state.filterSessions}
                    />
                ) : null}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./components/pages/Dashboard.jsx